import {
  useRecoilValue,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'
import {
  selectedItemAtom,
  itemSelector,
  itemAtomFamily,
  itemsLockedAtom,
  unsavedChangesAtom,
} from 'atoms'
import { Button, ColorPicker, Input, Clipboard } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useDuplicateItem } from 'hooks'
import { LockAsset } from 'components/editor/PropertiesMenu/LockAsset'
import { HideInPdf } from '@/components/editor/PropertiesMenu/HideInPdf'
import { useTranslation } from 'react-i18next'

export const ShapeDrawer = () => {
  const { t } = useTranslation()
  const selectedItem = useRecoilValue(selectedItemAtom)
  const itemsLocked = useRecoilValue(itemsLockedAtom)

  const duplicateItem = useDuplicateItem()

  const [width, setWidth] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'width' }),
  )
  const [height, setHeight] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'height' }),
  )
  const [color, setColor] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'edits.backgroundColor' }),
  )
  const [circle, setCircle] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'edits.circle' }),
  )

  const toggleShape = (shape) => setCircle(shape === 'circle')

  const resetItem = useResetRecoilState(itemAtomFamily(selectedItem))
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  const deleteItem = () => {
    resetItem()
    setUnsavedChanges(true)
  }

  return (
    <Flex column padding="0 12px">
      <Button
        center
        margin="0 0 16px"
        onClick={duplicateItem}
        disabled={itemsLocked}
      >
        {t('editor.menus.properties.elements.shapeDrawer.duplicateShapeButton')}
      </Button>
      <Button center margin="0 0 16px" onClick={deleteItem}>
        {t('editor.menus.properties.elements.shapeDrawer.deleteShapeButton')}
      </Button>
      <Clipboard />
      <LockAsset selectedItem={selectedItem} />
      <HideInPdf selectedItem={selectedItem} />
      <Text margin="0 0 16px">
        {t('editor.menus.properties.elements.shapeDrawer.sizeText')}
      </Text>
      <Flex margin="0 0 24px">
        <Input
          value={[width, setWidth]}
          label={t('editor.menus.properties.elements.shapeDrawer.widthLabel')}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
          disabled={itemsLocked}
        />
        <Input
          value={[height, setHeight]}
          label={t('editor.menus.properties.elements.shapeDrawer.heightLabel')}
          placeholder="100"
          type="number"
          disabled={itemsLocked}
        />
      </Flex>
      <Text margin="0 0 16px">
        {t('editor.menus.properties.elements.shapeDrawer.shapeFillText')}
      </Text>
      <ColorPicker
        label={t('editor.menus.properties.elements.shapeDrawer.colorLabel')}
        color={[color, setColor]}
        resetButton
        margin="0 0 16px 0"
      />
      <Flex column margin="0 0 16px">
        <Text margin="0 0 16px">
          {t('editor.menus.properties.elements.shapeDrawer.shapeText')}
        </Text>
        <Flex>
          <Button
            width="42px"
            height="42px"
            padding="0 0 0 4px"
            margin="4px"
            icon="editorSquareImage"
            disabled={!circle}
            onClick={() => toggleShape('square')}
          />
          <Button
            width="42px"
            height="42px"
            padding="0 0 0 4px"
            margin="4px"
            icon="editorOvalImage"
            disabled={circle}
            onClick={() => toggleShape('circle')}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}

export default ShapeDrawer
